import React from "react";
import type { Theme } from "@mui/material/styles";
import * as AlertModule from "@mui/material/Alert/index.js";
import * as BoxModule from "@mui/material/Box/index.js";
import * as ButtonModule from "@mui/material/Button/index.js";
import * as CardModule from "@mui/material/Card/index.js";
import * as CardContentModule from "@mui/material/CardContent/index.js";
import * as StepModule from "@mui/material/Step/index.js";
import * as StepLabelModule from "@mui/material/StepLabel/index.js";
import * as StepperModule from "@mui/material/Stepper/index.js";
import * as TypographyModule from "@mui/material/Typography/index.js";
import { resolveDefault } from "../../utils/moduleInterop";
import ResponsiveTypography from "../../components/ResponsiveTypography";

const Alert = resolveDefault<any>(AlertModule);
const Box = resolveDefault<any>(BoxModule);
const Button = resolveDefault<any>(ButtonModule);
const Card = resolveDefault<any>(CardModule);
const CardContent = resolveDefault<any>(CardContentModule);
const Step = resolveDefault<any>(StepModule);
const StepLabel = resolveDefault<any>(StepLabelModule);
const Stepper = resolveDefault<any>(StepperModule);
const Typography = resolveDefault<any>(TypographyModule);

interface WebStepperProps {
	children: React.ReactNode;
	steps: string[];
	application_name: string;
	isStepperRequired: boolean;
	activeStep: number;
	handleNext: () => void;
	handleBack: () => void;
	isStepOptional?: (step: number) => boolean;
	isStepSkipped?: (step: number) => boolean;
	validationErrorMsg?: string;
	nextButtonName: string;
	backButtonName: string;
}

const WebStepper: React.FC<WebStepperProps> = ({
	children,
	steps,
	application_name,
	isStepperRequired,
	activeStep,
	handleNext,
	handleBack,
	isStepOptional,
	isStepSkipped,
	validationErrorMsg,
	nextButtonName,
	backButtonName,
}) => {
	return (
		<>
			<Card
				sx={{
					mb: 0.5,
					borderRadius: 1,
					marginLeft: "auto",
					marginRight: "auto",
					"@media (max-width: 960px)": {
						width: "100%",
					},
				}}
			>
				<CardContent>
					{application_name && (
						<ResponsiveTypography>
							{application_name}
						</ResponsiveTypography>
					)}
					{isStepperRequired && (
						<Stepper activeStep={activeStep} alternativeLabel>
							{steps.map((label, index) => {
								const stepProps: { completed?: boolean } = {};
								const labelProps: {
									optional?: React.ReactNode;
								} = {};

								if (isStepOptional && isStepOptional(index)) {
									labelProps.optional = (
										<Typography variant="caption">
											Optional
										</Typography>
									);
								}
								if (isStepSkipped && isStepSkipped(index)) {
									stepProps.completed = false;
								}
								return (
									<Step key={label} {...stepProps}>
										<StepLabel {...labelProps}>
											{label}
										</StepLabel>
									</Step>
								);
							})}
						</Stepper>
					)}
				</CardContent>
			</Card>
			{children}
			{validationErrorMsg && (
				<Alert severity="error" sx={{ mt: 2 }}>
					{validationErrorMsg}
				</Alert>
			)}
			<Box
				sx={{
					display: "flex",
					flexDirection: "row-reverse",
					mt: 2,
				}}
			>
				<Button
					size="small"
					color="primary"
					variant="contained"
					sx={{ marginRight: (theme: Theme) => theme.spacing(1) }}
					onClick={handleNext}
				>
					{nextButtonName}
				</Button>
				<Button
					size="small"
					onClick={handleBack}
					disabled={activeStep === 0}
					sx={{ marginRight: (theme: Theme) => theme.spacing(1) }}
				>
					{backButtonName}
				</Button>
			</Box>
		</>
	);
};

export default WebStepper;
